/**
 * Reading a QR code from one camera frame (T07-29): the browser's own `BarcodeDetector` when it
 * has one (Android Chrome — fast, native), else `jsQR` (Apache-2.0, pure JS — iPad Safari,
 * desktop). Both work on the pixels of a `<video>` drawn into a canvas; nothing leaves the tablet.
 */
import jsQR from "jsqr";

export interface Frame {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

/** jsQR on RGBA pixels → the text, or null when no QR code is in the frame. */
export function decodeFrame(frame: Frame): string | null {
  const hit = jsQR(frame.data, frame.width, frame.height, { inversionAttempts: "dontInvert" });
  return hit?.data ? hit.data : null;
}

interface DetectedBarcode {
  rawValue: string;
}

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}

interface BarcodeDetectorCtor {
  new (options: { formats: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

/** The native detector for QR codes, when this browser has one. */
export async function nativeQrDetector(): Promise<BarcodeDetectorLike | null> {
  const Ctor = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
  if (!Ctor) {
    return null;
  }
  try {
    const formats = (await Ctor.getSupportedFormats?.()) ?? ["qr_code"];
    return formats.includes("qr_code") ? new Ctor({ formats: ["qr_code"] }) : null;
  } catch {
    return null;
  }
}
